import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  DollarOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { membershipAdminService } from '../../services/membershipAdmin';
import { formatCurrency } from '../../utils/helpers';

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.message || fallback
);

const pick = (record, ...keys) => {
  for (const key of keys) {
    if (record?.[key] !== undefined && record?.[key] !== null) return record[key];
  }
  return undefined;
};

const statusColor = (status) => ({
  'Đợi Duyệt': 'gold',
  'Đã Duyệt': 'green',
  'Đã Xóa': 'default'
}[status] || 'blue');

const paymentStatusColor = (status) => ({
  'Chờ Thanh Toán': 'orange',
  'Đã Thanh Toán': 'green',
  'Không Cần Thanh Toán': 'blue',
  'Hủy': 'red'
}[status] || 'default');

const canApprovePayment = (record) => {
  const paymentStatus = pick(record, 'TrangThaiThanhToan', 'trangThaiThanhToan');
  return paymentStatus === 'Đã Thanh Toán' || paymentStatus === 'Không Cần Thanh Toán';
};

const RenewalManager = ({ onMemberDataChanged }) => {
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [reasonAction, setReasonAction] = useState(null);
  const [reason, setReason] = useState('');
  const [cashAction, setCashAction] = useState(null);
  const [cashAmount, setCashAmount] = useState(null);
  const [cashNote, setCashNote] = useState('');

  const fetchRenewals = useCallback(async () => {
    setLoading(true);
    try {
      const result = await membershipAdminService.getRenewals({
        search,
        trangThai: status,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      });

      if (!result?.success || !Array.isArray(result.data)) {
        throw new Error(result?.message || 'Dữ liệu đơn đăng ký/gia hạn không hợp lệ');
      }

      setRenewals(result.data);
      setTotal(Number(result.pagination?.total || 0));
    } catch (error) {
      setRenewals([]);
      setTotal(0);
      message.error(getErrorMessage(error, 'Không thể tải danh sách đơn đăng ký/gia hạn'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, search, status]);

  useEffect(() => {
    fetchRenewals();
  }, [fetchRenewals]);

  const notifyMemberDataChanged = () => {
    if (typeof onMemberDataChanged === 'function') {
      onMemberDataChanged();
    }
  };

  const approve = async (record) => {
    const id = pick(record, 'IDDangKyGiaHanThanhVien', 'idDangKyGiaHanThanhVien', 'id');
    if (!id) return;

    if (!canApprovePayment(record)) {
      message.warning('Chỉ được duyệt khi đơn đã thanh toán hoặc không cần thanh toán.');
      return;
    }

    setProcessingId(id);
    try {
      const result = await membershipAdminService.approveRenewal(id);
      if (result?.success === false) throw new Error(result.message || 'Không thể duyệt đơn');
      message.success(result?.message || 'Duyệt gia hạn thành viên thành công');
      notifyMemberDataChanged();
      fetchRenewals();
    } catch (error) {
      message.error(getErrorMessage(error, 'Không thể duyệt đơn'));
    } finally {
      setProcessingId(null);
    }
  };

  const openCashAction = (record) => {
    const requiredAmount = Number(pick(record, 'SoTienPhaiThu', 'soTienPhaiThu', 'GiaGoi', 'Gia') || 0);
    setCashAction(record);
    setCashAmount(requiredAmount > 0 ? requiredAmount : null);
    setCashNote('');
  };

  const closeCashAction = () => {
    if (processingId !== null) return;
    setCashAction(null);
    setCashAmount(null);
    setCashNote('');
  };

  const submitCashPayment = async () => {
    if (!cashAction) return;

    const id = pick(cashAction, 'IDDangKyGiaHanThanhVien', 'idDangKyGiaHanThanhVien', 'id');
    const requiredAmount = Number(pick(cashAction, 'SoTienPhaiThu', 'soTienPhaiThu') || 0);
    const amount = Number(cashAmount);

    if (!id) return;
    if (!Number.isFinite(amount) || amount <= 0) {
      message.error('Số tiền nhận phải lớn hơn 0.');
      return;
    }
    if (requiredAmount > 0 && amount !== requiredAmount) {
      message.error(`Số tiền mặt phải đúng bằng ${formatCurrency(requiredAmount)}.`);
      return;
    }

    setProcessingId(id);
    try {
      const result = await membershipAdminService.confirmCashPayment(id, amount, cashNote);
      if (result?.success === false) {
        throw new Error(result.message || 'Không thể xác nhận thanh toán tiền mặt');
      }
      message.success(result?.message || 'Xác nhận thanh toán tiền mặt thành công');
      setCashAction(null);
      setCashAmount(null);
      setCashNote('');
      fetchRenewals();
    } catch (error) {
      message.error(getErrorMessage(error, 'Không thể xác nhận thanh toán tiền mặt'));
    } finally {
      setProcessingId(null);
    }
  };

  const openReasonAction = (type, record) => {
    setReason('');
    setReasonAction({ type, record });
  };

  const closeReasonAction = () => {
    if (processingId !== null) return;
    setReasonAction(null);
    setReason('');
  };

  const submitReasonAction = async () => {
    if (!reasonAction) return;
    const id = pick(
      reasonAction.record,
      'IDDangKyGiaHanThanhVien',
      'idDangKyGiaHanThanhVien',
      'id'
    );
    if (!id) return;

    setProcessingId(id);
    try {
      const result = reasonAction.type === 'revoke'
        ? await membershipAdminService.revokeRenewalApproval(id, reason)
        : await membershipAdminService.deleteRenewal(id, reason);

      if (result?.success === false) {
        throw new Error(result.message || 'Không thể xử lý đơn');
      }

      message.success(result?.message || (
        reasonAction.type === 'revoke'
          ? 'Hoàn tác duyệt gia hạn thành công'
          : 'Xóa đơn đăng ký gia hạn thành công'
      ));

      if (reasonAction.type === 'revoke') {
        notifyMemberDataChanged();
      }

      setReasonAction(null);
      setReason('');
      fetchRenewals();
    } catch (error) {
      message.error(getErrorMessage(error, 'Không thể xử lý đơn'));
    } finally {
      setProcessingId(null);
    }
  };

  const columns = useMemo(() => [
    {
      title: 'ID đơn',
      key: 'registrationId',
      width: 90,
      fixed: 'left',
      render: (_, record) => pick(record, 'IDDangKyGiaHanThanhVien', 'idDangKyGiaHanThanhVien', 'id')
    },
    {
      title: 'Mã GTV',
      dataIndex: 'MaThanhToan',
      width: 145,
      render: (value) => value || '-'
    },
    {
      title: 'Loại',
      dataIndex: 'LoaiDangKy',
      width: 110,
      render: (value) => value || '-'
    },
    {
      title: 'Doanh nghiệp',
      dataIndex: 'TenDoanhNghiep',
      width: 220,
      ellipsis: true
    },
    {
      title: 'Gói',
      dataIndex: 'TenGoi',
      width: 210,
      ellipsis: true
    },
    {
      title: 'Số tiền',
      key: 'paymentAmounts',
      width: 210,
      render: (_, record) => {
        const required = Number(pick(record, 'SoTienPhaiThu', 'soTienPhaiThu', 'GiaGoi', 'Gia') || 0);
        const paid = Number(pick(record, 'SoTienDaThanhToan', 'soTienDaThanhToan') || 0);
        const remaining = Number(
          pick(record, 'SoTienConThieu', 'soTienConThieu') ?? Math.max(required - paid, 0)
        );

        const rowStyle = {
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          lineHeight: '22px',
          whiteSpace: 'nowrap'
        };

        return (
          <div style={{ minWidth: 175 }}>
            <div style={rowStyle}>
              <span style={{ color: '#8c8c8c' }}>Phải thu:</span>
              <strong>{formatCurrency(required)}</strong>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#8c8c8c' }}>Đã thu:</span>
              <strong style={{ color: paid > 0 ? '#16a34a' : undefined }}>
                {formatCurrency(paid)}
              </strong>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#8c8c8c' }}>Còn thiếu:</span>
              <strong style={{ color: remaining > 0 ? '#d97706' : '#16a34a' }}>
                {formatCurrency(remaining)}
              </strong>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Thanh toán',
      key: 'paymentSummary',
      width: 210,
      render: (_, record) => {
        const paymentMethod = pick(record, 'PhuongThucThanhToan', 'phuongThucThanhToan') || '-';
        const paymentStatus = pick(record, 'TrangThaiThanhToan', 'trangThaiThanhToan') || 'Chưa xác định';

        return (
          <div style={{ lineHeight: '24px', whiteSpace: 'nowrap' }}>
            <div>
              <span style={{ color: '#8c8c8c', marginRight: 6 }}>Phương thức:</span>
              <strong>{paymentMethod}</strong>
            </div>
            <div style={{ marginTop: 4 }}>
              <span style={{ color: '#8c8c8c', marginRight: 6 }}>Trạng thái:</span>
              <Tag color={paymentStatusColor(paymentStatus)} style={{ marginInlineEnd: 0 }}>
                {paymentStatus}
              </Tag>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Thời hạn',
      dataIndex: 'ThoiHanThang',
      width: 105,
      render: (value) => value ? `${value} tháng` : '-'
    },
    {
      title: 'Trạng thái',
      dataIndex: 'TrangThai',
      width: 120,
      render: (value) => <Tag color={statusColor(value)}>{value || '-'}</Tag>
    },
    {
      title: <strong>Thao tác</strong>,
      key: 'actions',
      width: 300,
      fixed: 'right',
      render: (_, record) => {
        const id = pick(record, 'IDDangKyGiaHanThanhVien', 'idDangKyGiaHanThanhVien', 'id');
        const recordStatus = pick(record, 'TrangThai', 'trangThai');
        const paymentStatus = pick(record, 'TrangThaiThanhToan', 'trangThaiThanhToan');
        const paymentMethod = pick(record, 'PhuongThucThanhToan', 'phuongThucThanhToan');
        const busy = processingId === id;
        const approvable = canApprovePayment(record);
        const canConfirmCash = recordStatus === 'Đợi Duyệt' &&
          paymentStatus === 'Chờ Thanh Toán' &&
          paymentMethod !== 'Chuyển Khoản';

        if (recordStatus === 'Đợi Duyệt') {
          const approveTooltip = approvable
            ? 'Duyệt hồ sơ và kích hoạt thời hạn thành viên'
            : `Chưa thể duyệt: ${paymentStatus || 'chưa xác định trạng thái thanh toán'}`;

          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 7,
                whiteSpace: 'nowrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {canConfirmCash && (
                  <Tooltip title="Xác nhận đã nhận đủ tiền mặt cho đơn này" placement="top">
                    <Button
                      size="middle"
                      disabled={processingId !== null}
                      loading={busy}
                      icon={<DollarOutlined />}
                      onClick={() => openCashAction(record)}
                      style={{ fontWeight: 600 }}
                    >
                      Thu tiền mặt
                    </Button>
                  </Tooltip>
                )}

                <Tooltip title={approveTooltip} placement="top">
                  <span style={{ display: 'inline-block' }}>
                    <Popconfirm
                      title="Duyệt đơn thành viên"
                      description="Hệ thống sẽ cộng thời hạn gói và chuyển thành viên sang Hoạt Động."
                      okText="Xác nhận duyệt"
                      cancelText="Hủy"
                      disabled={!approvable}
                      onConfirm={() => approve(record)}
                    >
                      <Button
                        type="primary"
                        size="middle"
                        loading={busy}
                        disabled={!approvable || processingId !== null}
                        icon={<CheckOutlined />}
                        style={approvable ? {
                          background: '#16a34a',
                          borderColor: '#16a34a',
                          color: '#ffffff',
                          fontWeight: 700,
                          boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)'
                        } : {
                          background: '#f5f5f5',
                          borderColor: '#d9d9d9',
                          color: '#8c8c8c',
                          fontWeight: 600,
                          boxShadow: 'none',
                          cursor: 'not-allowed'
                        }}
                      >
                        {approvable ? 'Duyệt thành viên' : 'Chưa thể duyệt'}
                      </Button>
                    </Popconfirm>
                  </span>
                </Tooltip>
              </div>

              <div>
                <Tooltip title="Xóa mềm đơn đang đợi duyệt" placement="top">
                  <Button
                    danger
                    size="middle"
                    disabled={processingId !== null}
                    icon={<DeleteOutlined />}
                    onClick={() => openReasonAction('delete', record)}
                  >
                    Xóa
                  </Button>
                </Tooltip>
              </div>
            </div>
          );
        }

        if (recordStatus === 'Đã Duyệt') {
          return (
            <Tooltip title="Hoàn tác lần duyệt này và điều chỉnh lại thời hạn thành viên" placement="top">
              <Button
                size="middle"
                disabled={processingId !== null}
                loading={busy}
                icon={<RollbackOutlined />}
                onClick={() => openReasonAction('revoke', record)}
                style={{ fontWeight: 600 }}
              >
                Hoàn tác duyệt
              </Button>
            </Tooltip>
          );
        }

        return <span style={{ color: '#8c8c8c' }}>Không có thao tác</span>;
      }
    }
  ], [processingId]);

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          allowClear
          value={searchInput}
          prefix={<SearchOutlined />}
          placeholder="Tìm thành viên, mã GTV, gói, thanh toán..."
          style={{ width: 340 }}
          onChange={(event) => setSearchInput(event.target.value)}
          onPressEnter={() => {
            setCurrentPage(1);
            setSearch(searchInput.trim());
          }}
        />
        <Select
          allowClear
          value={status}
          placeholder="Tất cả trạng thái đơn"
          style={{ width: 185 }}
          options={['Đợi Duyệt', 'Đã Duyệt', 'Đã Xóa'].map((item) => ({ value: item, label: item }))}
          onChange={(value) => {
            setStatus(value);
            setCurrentPage(1);
          }}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={() => {
            setCurrentPage(1);
            setSearch(searchInput.trim());
          }}
        >
          Tìm kiếm
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            setSearchInput('');
            setSearch('');
            setStatus(undefined);
            setCurrentPage(1);
          }}
        >
          Đặt lại
        </Button>
      </Space>

      <Table
        rowKey={(record) => String(pick(record, 'IDDangKyGiaHanThanhVien', 'idDangKyGiaHanThanhVien', 'id'))}
        columns={columns}
        dataSource={renewals}
        loading={loading}
        scroll={{ x: 1720 }}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (value) => `Tổng ${value} đơn`,
          onChange: (page, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setCurrentPage(1);
            } else {
              setCurrentPage(page);
            }
          }
        }}
      />

      <Modal
        title="Xác nhận đã thu tiền mặt"
        open={Boolean(cashAction)}
        okText="Xác nhận đã thu"
        cancelText="Hủy"
        confirmLoading={processingId !== null}
        onOk={submitCashPayment}
        onCancel={closeCashAction}
      >
        <p>
          Mã thanh toán: <strong>{pick(cashAction, 'MaThanhToan', 'maThanhToan') || '-'}</strong>
        </p>
        <p>
          Số tiền phải thu: <strong>{formatCurrency(Number(pick(cashAction, 'SoTienPhaiThu', 'GiaGoi', 'Gia') || 0))}</strong>
        </p>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6 }}>Số tiền thực nhận</div>
          <InputNumber
            min={1}
            precision={0}
            style={{ width: '100%' }}
            value={cashAmount}
            onChange={setCashAmount}
          />
        </div>
        <div>
          <div style={{ marginBottom: 6 }}>Ghi chú</div>
          <Input.TextArea
            rows={3}
            maxLength={250}
            showCount
            value={cashNote}
            placeholder="Ví dụ: Thu tiền mặt tại văn phòng"
            onChange={(event) => setCashNote(event.target.value)}
          />
        </div>
      </Modal>

      <Modal
        title={reasonAction?.type === 'revoke' ? 'Hoàn tác duyệt gia hạn' : 'Xóa đơn đăng ký/gia hạn'}
        open={Boolean(reasonAction)}
        okText={reasonAction?.type === 'revoke' ? 'Hoàn tác duyệt' : 'Xóa đơn'}
        cancelText="Hủy"
        okButtonProps={{ danger: reasonAction?.type === 'delete' }}
        confirmLoading={processingId !== null}
        onOk={submitReasonAction}
        onCancel={closeReasonAction}
      >
        <p>
          {reasonAction?.type === 'revoke'
            ? 'Đơn sẽ được đưa về trạng thái Đợi Duyệt và thời hạn đã cộng sẽ bị trừ lại.'
            : 'Chỉ đơn Đợi Duyệt mới xóa được. Đây là xóa mềm và đơn sẽ chuyển sang Đã Xóa.'}
        </p>
        <Input.TextArea
          rows={4}
          maxLength={500}
          showCount
          value={reason}
          placeholder="Lý do (không bắt buộc)"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </>
  );
};

export default RenewalManager;
